import { inject, Injectable } from '@angular/core';
import { CustomerStore, SimpleMessageService, TrackingIdService, TradeTransactionStore, UserStore } from '@shared/data';
import { TranslateService } from '@ngx-translate/core';
import {
  CustomerType,
  ErrorDto,
  Handelsgeschaeft,
  HandelsgeschaeftExecuteResultDto,
  HandelsgeschaeftService,
  HandelsgeschaeftServiceChange,
  HandelsgeschaeftStatus
} from 'lion-api';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { saveAs } from 'file-saver';

@Injectable({
  providedIn: 'root'
})
export class InvestmentService {
  protected tradeTransactionStore = inject(TradeTransactionStore);
  private simpleMessageService = inject(SimpleMessageService);
  private readonly MESSAGE_KEY = 'tradeTransaction';
  private readonly DATE_IN_PAST_KEY = 'dateInPastKey';
  private readonly TRADE_TRANSACTION_CHANGE_KEY = 'tradeTransactionChangeContext';
  private translateService = inject(TranslateService);
  private handelsgeschaeftService = inject(HandelsgeschaeftService);
  private trackingIdService = inject(TrackingIdService);
  private router = inject(Router);

  private userStore = inject(UserStore);
  private customerStore = inject(CustomerStore);

  public getActiveTabFromUrl(validTabIds: string[], defaultTabId: string): string {
    const urlSegments = this.router.url.split('/');
    const lastSegment = urlSegments.pop() || ''; // Holt das letzte Segment oder einen leeren String

    if (validTabIds.includes(lastSegment)) {
      return lastSegment;
    }

    return defaultTabId;
  }

  processExecute(
    geschaeft: Handelsgeschaeft,
    isOverrideLinienueberziehung = false,
    isBestandEinhaltungOverride = false,
    isTagesgeldAufloesung = false
  ) {
    const hg: Handelsgeschaeft = { ...geschaeft };

    this.simpleMessageService.clearInlineMessage(this.TRADE_TRANSACTION_CHANGE_KEY);
    this.simpleMessageService.sendInlineInfoMessage(this.MESSAGE_KEY, this.translateService.instant('tradingBusiness.popUpMessages.hgConfirm'));

    this.tradeTransactionStore.updateTradeTransactionIsProcessing(true);
    this.tradeTransactionStore.disableMask(true);

    this.handelsgeschaeftService
      .handelsgeschaeftExecutePost(isOverrideLinienueberziehung, isBestandEinhaltungOverride, isTagesgeldAufloesung, hg)
      .pipe(finalize(() => this.tradeTransactionStore.updateTradeTransactionIsProcessing(false)))
      .subscribe({
        next: (response) => {
          if (
            (response.handelsgeschaeftExecuteResult?.requestId &&
              response.handelsgeschaeftExecuteResult?.requestId === this.tradeTransactionStore.tradeTransaction()?.requestId) ||
            (response.changeContext && response.changeContext.length > 0)
          ) {
            if (response.changeContext && response.changeContext.length == 0) {
              if (response.handelsgeschaeftExecuteResult?.hasLinienUeberziehung) {
                this.tradeTransactionStore.updateHasLinienUeberziehung(true);
                return;
              }

              if (response.handelsgeschaeftExecuteResult?.hasBestandUeberziehung) {
                this.tradeTransactionStore.updateHasBestandUeberziehung(true);
                return;
              }

              const murexResult = response.handelsgeschaeftExecuteResult;
              if (murexResult) {
                this.tradeTransactionStore.updateProcessingState(true);
                this.tradeTransactionStore.updateTradeTransactionIsProcessing(false);
                this.sendStatus(murexResult);
              }
            } else {
              this.simpleMessageService.clearInlineMessage(this.MESSAGE_KEY);

              const changedTransaction = response.changedHandelsgeschaeft;
              if (changedTransaction) {
                this.tradeTransactionStore.updateTradeTransaction(changedTransaction);
              }

              const interestChange = response.changeContext?.find((change) => change.change === HandelsgeschaeftServiceChange.ChangedInterestrate);
              if (interestChange) {
                const oldZinssatz = this.tradeTransactionStore
                  .computeEffectiveInterestRate(interestChange.alterZinssatz, hg.typ)
                  ?.toFixed(3)
                  .replace('.', ',');
                const newZinssatz = this.tradeTransactionStore
                  .computeEffectiveInterestRate(interestChange.neuerZinssatz, hg.typ)
                  ?.toFixed(3)
                  .replace('.', ',');

                this.simpleMessageService.sendInlineErrorMessage(
                  this.TRADE_TRANSACTION_CHANGE_KEY,
                  this.translateService.instant('tradingBusiness.changeContexMessages.CHANGED_INTERESTRATE', {
                    oldZinssatz: oldZinssatz,
                    newZinssatz: newZinssatz
                  })
                );

                this.tradeTransactionStore.disableMask(false);
                this.tradeTransactionStore.updateTradeTransactionIsProcessing(false);
              }
            }
          }
        },
        error: (response: HttpErrorResponse) => {
          const errorKey: string = response.error.errors.errors[0].key;
          this.simpleMessageService.sendInlineErrorMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('tradingBusiness.popUpMessages.' + errorKey)
          );
        }
      });
  }

  formatTradeTransactionDate(tradeTransactionDate: Date | undefined): Date | null {
    if (!tradeTransactionDate) {
      return null;
    }

    // Create a copy so we don't mutate the original object
    const normalizedDate = new Date(tradeTransactionDate);
    normalizedDate.setHours(0, 0, 0, 0);

    return normalizedDate;
  }

  showStartValutaHint(tradeTransaction: Handelsgeschaeft) {
    if (tradeTransaction.startValuta) {
      const currentDate = new Date();
      const startValuta = this.formatTradeTransactionDate(tradeTransaction.startValuta);

      if (startValuta) {
        startValuta.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);

        if (startValuta < currentDate) {
          this.simpleMessageService.sendInlineWarnMessage(
            this.DATE_IN_PAST_KEY,
            this.translateService.instant('tradingBusiness.popUpMessages.startValutaInpastError')
          );
        } else {
          this.simpleMessageService.clearInlineMessage(this.DATE_IN_PAST_KEY);
        }
      }
    }
  }

  sendStatus(result: HandelsgeschaeftExecuteResultDto) {
    if (result.handelsgeschaeftErrorText?.includes('|')) {
      const parts = result.handelsgeschaeftErrorText?.split('|');
      const key = parts[0];
      const directMessage = parts[1];

      if (key) {
        // Fall: "KEY|errMsg" -> Übersetze Key und füge errMsg als Parameter ein
        if (!this.userStore.isDzBankUser()) {
          const messageKey = this.translateService.instant('tradingBusiness.popUpMessages.' + key + '-extern');
          const message = messageKey !== 'tradingBusiness.popUpMessages.' + key + '-extern'
            ? messageKey
            : this.translateService.instant('tradingBusiness.popUpMessages.' + key, { errMsg: '' });
          this.simpleMessageService.sendInlineErrorMessage(this.MESSAGE_KEY, message);
        } else {
          const message = this.translateService.instant('tradingBusiness.popUpMessages.' + key, { errMsg: directMessage });
          this.simpleMessageService.sendInlineErrorMessage(this.MESSAGE_KEY, message);
        }
      } else {
        // Fall: "|errMsg" -> Zeige errMsg direkt an
        if (!this.userStore.isDzBankUser()) {
          const userType = this.userStore.userType();
          const messageKey = userType === 'fk'
            ? 'tradingBusiness.popUpMessages.failedInMurex-fk'
            : 'tradingBusiness.popUpMessages.failedInMurex-vr';
          this.simpleMessageService.sendInlineErrorMessage(
            this.MESSAGE_KEY,
            this.translateService.instant(messageKey)
          );
        } else {
          this.simpleMessageService.sendInlineErrorMessage(this.MESSAGE_KEY, directMessage);
        }
      }
      return;
    }

    if (result.handelsgeschaeftExecuteStatus) {
      const handelsgeschaeftExecuteStatus = result.handelsgeschaeftExecuteStatus;
      switch (handelsgeschaeftExecuteStatus) {
        case 'MSG_HG_COMPLETE_AENDERUNG': {
          const murexId = result.murexId ?? '';
          this.simpleMessageService.sendInlineSuccessMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('tradingBusiness.popUpMessages.hgCompleteChange', {
              oldGeschaeft: result.originalMurexId,
              newGeschaeft: murexId
            })
          );
          this.tradeTransactionStore.updateHandelsgeschaeftStatus(HandelsgeschaeftStatus.Corrected);
          this.tradeTransactionStore.updateCorrectedMurexId(murexId);
          break;
        }

        case 'MSG_HG_COMPLETE': {
          const murexId = result.murexId ?? '';
          this.tradeTransactionStore.updateHandelsgeschaeftStatus(HandelsgeschaeftStatus.Success);
          this.tradeTransactionStore.updateMurexId(murexId);
          this.simpleMessageService.sendInlineSuccessMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('tradingBusiness.popUpMessages.MSG_HG_COMPLETE', { murexID: murexId })
          );
          break;
        }

        case 'MSG_HG_COMPLETE_STORNO': {
          const murexId = result.murexId ?? '';
          this.simpleMessageService.sendInlineSuccessMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('tradingBusiness.popUpMessages.MSG_HG_COMPLETE_STORNO', { murexID: murexId })
          );
          this.tradeTransactionStore.updateHandelsgeschaeftStatus(HandelsgeschaeftStatus.Cancelled);
          break;
        }

        case 'MSG_HG_FAILED_IN_LION':
          if (result.handelsgeschaeftErrorText === 'dailyLimits') {
            const suffix = this.userStore.isDzBankUser() ? 'dz' : 'fk';
            const messageKey = 'tradingBusiness.popUpMessages.failedInLion-dailyLimits-' + suffix;
            this.simpleMessageService.sendInlineErrorMessage(this.MESSAGE_KEY, this.translateService.instant(messageKey));
          } else {
            this.simpleMessageService.sendInlineErrorMessage(
              this.MESSAGE_KEY,
              this.translateService.instant('tradingBusiness.popUpMessages.failedInLion')
            );
          }
          break;

        case 'MSG_HG_FAILED_IN_MUREX': {
          if (!this.userStore.isDzBankUser()) {
            const userType = this.userStore.userType();
            const messageKey = userType === 'fk'
              ? 'tradingBusiness.popUpMessages.failedInMurex-fk'
              : 'tradingBusiness.popUpMessages.failedInMurex-vr';
            this.simpleMessageService.sendInlineErrorMessage(
              this.MESSAGE_KEY,
              this.translateService.instant(messageKey)
            );
          } else {
            this.simpleMessageService.sendInlineErrorMessage(
              this.MESSAGE_KEY,
              this.translateService.instant('tradingBusiness.popUpMessages.failedInMurex')
            );
          }
          break;
        }

        case 'MSG_HG_TIMEOUT':
          this.simpleMessageService.sendInlineErrorMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('tradingBusiness.popUpMessages.hgTimeOut')
          );
          break;

        case 'MSG_HG_VALIDIERUNG':
          this.simpleMessageService.sendInlineErrorMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('tradingBusiness.popUpMessages.hgValidation')
          );
          break;

        case 'MSG_HG_LOCKED':
          this.simpleMessageService.sendInlineErrorMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('tradingBusiness.popUpMessages.MSG_HG_LOCKED')
          );
          break;

        case 'MSG_HG_HANDELSZEITEN':
          this.simpleMessageService.sendInlineErrorMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('tradingBusiness.popUpMessages.tradingTime')
          );
          break;

        case 'notAllowed':
          this.simpleMessageService.sendInlineErrorMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('tradingBusiness.popUpMessages.notAllowed')
          );
          break;
      }
    } else if (result.handelsgeschaeftErrorText) {
      const handelsgeschaeftErrorText = result.handelsgeschaeftErrorText;
      switch (handelsgeschaeftErrorText) {
        case 'MSG_HG_TAGESGELD_NICHT_EINDEUTIG':
          this.simpleMessageService.sendInlineErrorMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('tradingBusiness.popUpMessages.MSG_HG_TAGESGELD_NICHT_EINDEUTIG-extern')
          );
          break;

        case 'MSG_HG_CONCURRENT':
          this.simpleMessageService.sendInlineErrorMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('tradingBusiness.popUpMessages.hgConcurrent')
          );
          break;

        case 'MSG_HG_LIMITCHECK_FAILED':
          this.simpleMessageService.sendInlineErrorMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('tradingBusiness.popUpMessages.hgLimitCheckFailed')
          );
          break;

        case 'MSG_HG_LIMIT_EXCEEDED':
          this.simpleMessageService.sendInlineErrorMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('tradingBusiness.popUpMessages.hgLimitExceeded')
          );
          break;

        case 'MSG_HG_VERSIONCHECK_FAILED':
          this.simpleMessageService.sendInlineErrorMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('tradingBusiness.popUpMessages.versioncheckFailed')
          );
          break;
      }
    }
  }

  print(geschaeft: Handelsgeschaeft) {
    if (geschaeft.murexId != null) {
      this.handelsgeschaeftService.handelsgeschaeftPdfExportMurexIdGet(geschaeft.murexId, this.trackingIdService.generateTrackingId()).subscribe({
        next: (response) => {
          if (response) {
            const blob = new Blob([response], { type: 'application/pdf' });
            const customerType = geschaeft.kunde?.type;
            const isCorporate =
              this.userStore.isCorporateMode() ||
              customerType === CustomerType.Firmenkunde ||
              (customerType == null && this.customerStore.isCorporate());
            const prefix = isCorporate ? 'LICo' : 'LiOn';
            const filename = `${prefix}_${geschaeft.typ?.simpleHgType}_${geschaeft.typ?.richtung}_${geschaeft.murexId}`;
            saveAs(blob, `${filename}.pdf`);
          }
        },
        error: (err: ErrorDto) => {
          console.log(`Error while downloading pdf document: ${err.message}`);
          this.simpleMessageService.sendInlineErrorMessage(this.MESSAGE_KEY, this.translateService.instant('businessStatus.details.printError'));
        }
      });
    }
  }

  resetStateForDialog() {
    this.tradeTransactionStore.updateTradeTransactionIsProcessing(false);
    this.tradeTransactionStore.updateProcessingState(false);
    this.tradeTransactionStore.disableMask(false);

    this.simpleMessageService.clearInlineMessage(this.MESSAGE_KEY);
  }
}
